import { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Icon } from '../components/Layout'
import PostCard from '../components/PostCard'
import { useToast } from '../context/ToastContext'
import {
  getFeedPosts,
  likePost as apiLikePost,
  unlikePost as apiUnlikePost,
  updatePost as apiUpdatePost,
  deletePost as apiDeletePost,
} from '../services/posts'
import { postMatchesHashtag, normalizeTag } from '../utils/hashtags'
import { humanizeApiError } from '../services/client'

function PostSkeleton() {
  return (
    <div className="bg-surface-container border border-on-background/10 lg:neo-border p-4 lg:p-6 animate-pulse">
      <div className="flex gap-3 mb-4">
        <div className="w-10 h-10 bg-on-background/10 rounded-none flex-shrink-0" />
        <div className="flex-1 flex flex-col gap-2 pt-1">
          <div className="h-3 bg-on-background/10 w-32" />
          <div className="h-2.5 bg-on-background/10 w-20" />
        </div>
      </div>
      <div className="flex flex-col gap-2 mb-4">
        <div className="h-3 bg-on-background/10 w-full" />
        <div className="h-3 bg-on-background/10 w-5/6" />
      </div>
    </div>
  )
}

export default function HashtagPage() {
  const { tag } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const hashtag = `#${normalizeTag(tag)}`

  const [allPosts, setAllPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Pull a generous batch of posts and filter client-side — the backend
  // doesn't expose a hashtag query param, so this mirrors how hashtags are
  // already detected for highlighting in PostCard.
  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)

    async function load() {
      try {
        let cursor = null
        let collected = []
        let guard = 0
        do {
          const data = await getFeedPosts(cursor, 50)
          collected = collected.concat(data.posts ?? [])
          cursor = data.hasMore ? data.nextCursor : null
          guard += 1
        } while (cursor && guard < 10) // cap at ~500 posts scanned

        if (alive) setAllPosts(collected)
      } catch (err) {
        if (alive) setError(err?.message || 'Failed to load posts')
      } finally {
        if (alive) setLoading(false)
      }
    }
    load()
    return () => { alive = false }
  }, [tag])

  const taggedPosts = useMemo(
    () => allPosts.filter((p) => postMatchesHashtag(p, hashtag)),
    [allPosts, hashtag],
  )

  const handleHashtagClick = useCallback((nextHashtag) => {
    navigate(`/hashtag/${encodeURIComponent(normalizeTag(nextHashtag))}`)
  }, [navigate])

  const handleLike = async (postId) => {
    const post = allPosts.find((p) => p.id === postId)
    if (!post) return
    setAllPosts((prev) => prev.map((p) =>
      p.id === postId
        ? { ...p, liked: !p.liked, likes: p.liked ? (Number(p.likes) || 0) - 1 : (Number(p.likes) || 0) + 1 }
        : p
    ))
    try {
      if (post.liked) await apiUnlikePost(postId)
      else await apiLikePost(postId)
    } catch {
      setAllPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, liked: post.liked, likes: post.likes } : p)))
    }
  }

  const handleEditPost = async (post) => {
    const next = window.prompt('Edit post:', post.content ?? '')
    if (next === null || next.trim() === (post.content ?? '').trim()) return
    const prev = allPosts
    setAllPosts((p) => p.map((x) => (x.id === post.id ? { ...x, content: next.trim() } : x)))
    try {
      await apiUpdatePost(post.id, { content: next.trim(), categoryId: post.categoryId, isPublished: true })
    } catch (err) {
      setAllPosts(prev)
      toast.error(humanizeApiError(err))
    }
  }

  const handleDeletePost = async (post) => {
    const prev = allPosts
    setAllPosts((p) => p.filter((x) => x.id !== post.id))
    try {
      await apiDeletePost(post.id)
    } catch {
      setAllPosts(prev)
      toast.error('Could not delete post.')
    }
  }

  return (
    <div className="flex-1 lg:ml-[300px] w-full max-w-3xl flex flex-col gap-4 lg:gap-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-primary-container font-bold text-sm hover:underline self-start"
      >
        <Icon name="arrow_back" className="text-[18px]" /> Go back
      </button>

      <section className="bg-surface-container border border-on-background/10 lg:neo-border lg:neo-shadow p-4 lg:p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 lg:w-12 lg:h-12 bg-primary-container border border-on-background/20 lg:neo-border flex items-center justify-center flex-shrink-0">
            <Icon name="tag" className="text-[20px] lg:text-[24px] text-on-primary-container" />
          </div>
          <div className="min-w-0">
            <h1 className="font-extrabold uppercase tracking-tight leading-none text-[18px] lg:text-[24px] truncate">
              {hashtag}
            </h1>
            <p className="text-[11px] lg:text-[12px] text-on-surface-variant font-mono">
              {loading ? 'Loading…' : `${taggedPosts.length} ${taggedPosts.length === 1 ? 'post' : 'posts'}`}
            </p>
          </div>
        </div>
      </section>

      {error ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3 text-on-surface-variant">
          <span className="material-symbols-outlined text-[40px] opacity-30">wifi_off</span>
          <p className="font-bold text-sm uppercase tracking-widest opacity-50">Unable to load posts</p>
          <p className="text-xs opacity-40">{error}</p>
        </div>
      ) : loading ? (
        <div className="flex flex-col gap-4 lg:gap-6">
          <PostSkeleton />
          <PostSkeleton />
          <PostSkeleton />
        </div>
      ) : taggedPosts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-on-surface-variant">
          <Icon name="tag" className="text-[40px] opacity-30" />
          <p className="font-bold text-sm uppercase tracking-widest opacity-50">No posts with {hashtag} yet</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4 lg:gap-6">
          {taggedPosts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onLike={handleLike}
              onEdit={handleEditPost}
              onDelete={handleDeletePost}
              onHashtagClick={handleHashtagClick}
            />
          ))}
        </div>
      )}

      <div className="h-20 md:h-0" />
    </div>
  )
}
